import { getNewEmptyBalanceSheetObj } from '~/models/balance-sheet';
import { getNewEmptyIncomeStatementObj } from '~/models/income-statement';
import { getEmptyFinancialRatios } from '~/models/FinancialRatios';
import { employeesSelectors, levelSelectors, turnSelectors } from '~/store/selectors';
import { getTurnPhase, getTurnMonth, getCurrentQuarterNum } from '~/store/turn/selectors';
import { QUARTERS_ENDING_MONTHS, TURN_PHASES, LEVELS_TO_UNLOCK_FEATURES } from '~/lib/constants';
// ---- internal imports ----

import { isFinancialRatiosSubmittedForQuarter } from './financialRatios';
import { getAccountReceivablesForNextMonth } from './balanceSheet';
// ---- local imports ----

function hasErrorsInFinancialStatements(state) {
    return getErrorConfiguration(state).statement.length > 0;
}

function getDistributedProfits(state) {
    return state.financialStatements.distributedProfits;
}

export function didQuarterEnd(state) {
    const monthBeforeUpdate = getTurnMonth(state);
    const turnPhaseBeforeUpdate = getTurnPhase(state);

    const isLastMonthEndingQuarter = QUARTERS_ENDING_MONTHS.includes(monthBeforeUpdate);

    const isPhaseBeforeUpdateCustomer = turnPhaseBeforeUpdate === TURN_PHASES.CUSTOMERS;

    return isLastMonthEndingQuarter && isPhaseBeforeUpdateCustomer;
}

export function getContributedCapital(state) {
    return state.financialStatements.contributedCapital;
}

// There may be inital assets other than contributed capital
export function getInitialTotalAssets(state) {
    return getContributedCapital(state);
}

export function getOpeningInventoryFor(state, quarterNum) {
    return state.financialStatements.openingInventoryByQuarterNum[quarterNum];
}

export function getFinancialStatements(state) {
    return state.financialStatements;
}

export function getFinancialStatementsFor(state, quarterNum) {
    return (
        state.financialStatements.statementsByQuarterNum[quarterNum] ?? {
            balanceSheet: getNewEmptyBalanceSheetObj(),
            incomeStatement: getNewEmptyIncomeStatementObj(),
            financialRatios: getEmptyFinancialRatios(),
        }
    );
}

export function getSystemFinancialStatementsFor(state, quarterNum) {
    return state.financialStatements?.systemStatementsByQuarterNum[quarterNum] ?? {};
}

export function isFinancialStatementsSubmittedForQuarter(state, quarterNum) {
    return state.financialStatements?.isSubmittedByQuarterNum[quarterNum] ?? false;
}

export function getLostInventoryForQuarter(state, quarterNum) {
    return state.financialStatements.systemStatementsByQuarterNum[quarterNum].incomeStatement.lostInventory;
}

export function hasLostInventoryForQuarter(state, quarterNum) {
    return getLostInventoryForQuarter(state, quarterNum) > 0;
}

export function canSubmitFS(state) {
    return !hasErrorsInFinancialStatements(state);
}

export function shallAutomaticallySubmitFinancialStatement(state) {
    const hasReachedLevelToEditFinancialStatements =
        levelSelectors.getLevelNumber(state) >= LEVELS_TO_UNLOCK_FEATURES.FINANCIAL_STATEMENTS_EDIT;

    return !hasReachedLevelToEditFinancialStatements || employeesSelectors.hasFinancialStatementsAutomatorHired(state);
}

export function isFinancialStatementsAutomatedForQuarter(state, quarterNum) {
    return state.financialStatements.isFinancialStatementsAutomatedByQuarter[quarterNum];
}

export function getHasAttemptedToSubmitFinancialStatements(state) {
    return state.financialStatements.hasAttemptedToSubmitFinancialStatements;
}

export function getErrorConfiguration(state) {
    return state.financialStatements.errorConfiguration;
}

export function isCorrectingErrorsAfterSubmissionAttempt(state) {
    const hasErrors = hasErrorsInFinancialStatements(state);
    const hasAttemptedToSubmitFinancialStatements = getHasAttemptedToSubmitFinancialStatements(state);
    return hasErrors && hasAttemptedToSubmitFinancialStatements;
}

export function getErrorStatement(state) {
    return getErrorConfiguration(state).statement;
}

export function getErrorMessage(state) {
    return getErrorConfiguration(state).message;
}

export function shouldDisplayFinancialStatementsNotSubmittedPopup(state) {
    const currentQuarterNum = getCurrentQuarterNum(state);
    const isFinancialStatementsEditingLocked =
        levelSelectors.getLevelNumber(state) < LEVELS_TO_UNLOCK_FEATURES.FINANCIAL_STATEMENTS_EDIT;

    if (currentQuarterNum == 1 || isFinancialStatementsEditingLocked) {
        return false;
    }

    const hasSubmittedFinanicalStatementsForLastQuarter = state.financialStatements.isSubmittedByQuarterNum[currentQuarterNum - 1] ?? false;

    if (!hasSubmittedFinanicalStatementsForLastQuarter) {
        return true;
    }

    const hasSubmittedFinancialRatios = isFinancialRatiosSubmittedForQuarter(state, currentQuarterNum - 1);

    if (!hasSubmittedFinancialRatios) {
        return true;
    }
}

export function getDisplayedStatement(state) {
    return state.financialStatements.displayedStatement;
}

export function getOpeningRetainedEarningsByMonth(state) {
    return state.financialStatements.openingRetainedEarningsByMonth;
}

export function hasDistributedProfitForCurrentMonth(state) {
    const distributedProfits = getDistributedProfits(state);
    const currentDate = new Date(turnSelectors.getTurnDate(state));

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;

    if (distributedProfits[year] && distributedProfits[year][month]) {
        return true;
    }

    return false;
}

export function getDistributedProfitFor(state, dateStr) {
    const { month, year } = turnSelectors.getMonthAndYear(state, dateStr);

    const distributedProfits = getDistributedProfits(state);

    return distributedProfits[year]?.[month] ?? 0;
}

export function getOpeningRetainedEarningsFor(state, dateStr) {
    const { month, year } = turnSelectors.getMonthAndYear(state, dateStr);

    return getOpeningRetainedEarningsByMonth(state)[year][month];
}

export function getQuarterOpeningRetainedEarnings(state, quarter) {
    if (quarter.num === 1) {
        return 0;
    }

    const { startDate } = quarter;
    const year = startDate.getFullYear();
    const startingMonth = startDate.getMonth() + 1;

    return getOpeningRetainedEarningsByMonth(state)[year][startingMonth];
}

export function getCurrentRetainedEarnings(state) {
    return state.financialStatements.currentRetainedEarnings;
}

export function getProfitDistributedIn(state, quarter) {
    const { startDate } = quarter;
    const year = startDate.getFullYear();
    const startingMonth = startDate.getMonth() + 1;
    const distributionMonths = [startingMonth, startingMonth + 1, startingMonth + 2];

    const distributedProfits = getDistributedProfits(state);
    const profitDistributedInPeriod = distributionMonths.reduce(
        (total, month) => total + (distributedProfits[year]?.[month] ?? 0),
        0,
    );

    return profitDistributedInPeriod;
}

export function getFirstQuarterWithProfitDistribution(state) {
    const distributedProfits = getDistributedProfits(state);

    const yearsWtihProfitDistribution = Object.keys(distributedProfits);

    if (yearsWtihProfitDistribution.length === 0) {
        return null;
    }

    const firstYearWithProfitDistribution = yearsWtihProfitDistribution[0];
    const monthsWithProfitDistribution = Object.keys(distributedProfits[firstYearWithProfitDistribution]);
    const firstMonthWithProfitDistribution = monthsWithProfitDistribution[0];

    const currDate = new Date(Date.now());
    const currYear = currDate.getFullYear();

    const firstQuarterWithProfitDistribution =
        (currYear - firstYearWithProfitDistribution) * 4 + Math.ceil(firstMonthWithProfitDistribution / 4);

    return firstQuarterWithProfitDistribution;
}
